// @flow
import injectScript from 'mz-utils/injectScript'
import delay from 'mz-utils/delay'
import StripeError, { ERROR_CODES } from './StripeError'

// Types
type Owner = { name?: string }

type UserDetails = {
  firstName: ?string,
  lastName: ?string,
}

type UpdateStateParams = {
  show3DSecure: boolean,
  threeDSecureURL: string,
}

export type StripeConfig = {
  currencies: Array<string>,
  origin: string,
  country: string,
  apiKey: string,
  default?: boolean,
}

export type Card = {
  exp_month: number,
  exp_year: number,
  brand: string,
  country: string,
  cvc_check: string,
  funding: string,
  last4: string,
  three_d_secure: string,
}

export type StripePaymentIntent = Object // TODO

export type StripeSource = {
  id: string,
  object: 'source',
  card?: Card,
  owner?: Owner,
  status: 'chargeable' | 'failed' | 'pending',
  type: 'card' | 'three_d_secure',
  amount?: number,
  currency?: string,
  redirect?: {
    return_url: string,
    status: string,
    url: string,
  },
  three_d_secure?: ?{
    card: string,
    brand: string,
    exp_month: number,
    exp_year: number,
    last4: string,
    three_d_secure: string,
  },
}

type PaymentRequestParams = {
  country: string,
  currency: string,
  total: {
    label: string,
    amount: number,
  },
  requestPayerName: boolean,
  requestPayerEmail: boolean,
}

type PaymentRequestEvent = {
  complete: (string) => void,
  paymentMethod: { id: string },
}

export type PaymentRequest = {
  canMakePayment: () => ?{ applePay: boolean },
  on: (string, (event: PaymentRequestEvent) => any) => void,
  once: (string, (event: PaymentRequestEvent) => any) => void,
  off: (string, (event: PaymentRequestEvent) => any) => void,
  show: () => void,
}

export type ElementsObject = {
  create: (any, any) => StripeElement,
}

export type StripeIntent = {
  id: string,
}

export type StripeObject = {
  elements: (any) => ElementsObject,
  paymentRequest: (PaymentRequestParams) => PaymentRequest,
  confirmCardSetup: (
    clientSecret: string,
    options: { payment_method: any }
  ) => Promise<{
    error: StripeError,
    setupIntent: StripeIntent,
  }>,
  confirmCardPayment: (
    clientSecret: string,
    options: { payment_method: any }
  ) => Promise<{
    error: StripeError,
    paymentIntent: StripeIntent,
  }>,
  createSource: (
    mixed,
    ?mixed
  ) => Promise<{
    source: StripeSource,
    error: StripeError,
  }>,
  retrieveSource: ({ id: string, client_secret: string }) => Promise<{
    source: StripeSource,
    error: StripeError,
  }>,
}

export type StripeElement = {
  mount: (any) => any,
  on: (string, (any) => any) => any,
  blur: () => any,
  clear: () => any,
  destroy: () => any,
  focus: () => any,
  unmount: () => any,
  update: () => any,
  __error?: ?any,
}

type ElementTypes =
  | 'cardNumber'
  | 'cardExpiry'
  | 'cardCvc'
  | 'paymentRequestButton'

export type PaymentIntentAuthOptions = {|
  stripe: ?StripeObject,
  currency?: string,
  paymentMethodId?: string,
  paymentIntentData: { client_secret: string },
  elements?: ?{ [ElementTypes]: StripeElement },
  shouldSecurePayment?: ?boolean,
  userDetails?: UserDetails,
  cancelAuthTrigger?: { cancel: boolean },
  postPayment?: boolean,
|}

/**
 * Save stripe inject promise to avoid
 * multiple script injections when using
 * more than one form.
 */
let INJECT_STRIPE_PROMISE = null
export const injectStripeOnce = () => {
  if (!INJECT_STRIPE_PROMISE) {
    INJECT_STRIPE_PROMISE = injectScript('https://js.stripe.com/v3/')
    return INJECT_STRIPE_PROMISE
  }
  return INJECT_STRIPE_PROMISE
}

export const initStripe = async (
  stripeApiKey: string
): Promise<StripeObject> => {
  if (!stripeApiKey) {
    throw new StripeError(ERROR_CODES.not_stripe_api_key)
  }

  // Inject Stripe script to the head if it is not defined
  if (!window.Stripe) {
    await injectStripeOnce()
    if (!window.Stripe) {
      throw new StripeError(ERROR_CODES.stripe_not_initialized)
    }
  }

  return window.Stripe(stripeApiKey)
}

export const isSourceSupportThreeDS = (source: StripeSource) => {
  if (!source || !source.card || !source.card.three_d_secure) {
    return false
  }
  const { card } = source
  return (
    card.three_d_secure === 'required' ||
    card.three_d_secure === 'recommended' ||
    card.three_d_secure === 'optional'
  )
}

export const isSourceRequireThreeDS = (source: StripeSource) =>
  source && source.card && source.card.three_d_secure === 'required'

export const isAuthorizedSourceRequireThreeDS = (source: StripeSource) =>
  source &&
  source.three_d_secure &&
  source.three_d_secure.three_d_secure === 'required'

/**
 * Creates raw stripe source
 */
export const createRawStripeSource = async ({
  stripe,
  elements,
  userDetails,
}: {|
  stripe: ?StripeObject,
  elements: ?{ [ElementTypes]: StripeElement },
  userDetails?: UserDetails,
|}): Promise<StripeSource> => {
  // Check that stripe was initialized
  if (!stripe || !elements) {
    throw new StripeError(ERROR_CODES.stripe_not_initialized)
  }

  // Create a source
  const owner = userDetails
    ? { name: `${userDetails.firstName || ''} ${userDetails.lastName || ''}` }
    : {}
  const { source, error } = await stripe.createSource(elements.cardNumber, {
    owner,
  })
  if (error) {
    throw new StripeError(error.code, null, error.message)
  }

  return source
}

/**
 * Authorize given payment intent and make it to be ready to charge
 * @return {Promise}
 */
export const createAuthorizedPaymentIntent = async ({
  stripe,
  paymentMethodId,
  paymentIntentData,
  userDetails,
  elements,
  postPayment,
}: PaymentIntentAuthOptions): Promise<StripeIntent> => {
  // Check that stripe was initialized
  if (!stripe || (!paymentMethodId && !elements)) {
    throw new StripeError(ERROR_CODES.stripe_not_initialized)
  }

  const owner = userDetails
    ? { name: `${userDetails.firstName || ''} ${userDetails.lastName || ''}` }
    : {}

  const paymentMethod = paymentMethodId || {
    card: elements?.cardNumber || '',
    billing_details: owner,
  }

  const result = postPayment
    ? await stripe.confirmCardSetup(paymentIntentData.client_secret, {
        payment_method: paymentMethod,
      })
    : await stripe.confirmCardPayment(paymentIntentData.client_secret, {
        payment_method: paymentMethod,
      })

  if (result.error) {
    const { error } = result
    throw new StripeError(error.code, null, error.message)
  }

  const intent = result.setupIntent ?? result.paymentIntent
  if (intent?.status === 'succeeded' || intent?.status === 'requires_capture') {
    return intent
  }

  throw new StripeError(
    null,
    null,
    `payment finished with an unkown status: ${intent.status}`
  )
}

/**
 * A function to create stripe source for given set of elements. Eventually
 * returns a Source object or rejects with a StripeError object.
 * @return {Promise<Source>}
 */
export const createAuthorizedStripeSource = async ({
  stripe,
  currency,
  elements,
  shouldSecurePayment,
  stripeChargeAmount,
  userDetails,
  updateState,
  cancelAuthTrigger,
}: {|
  stripe: ?StripeObject,
  currency: string,
  elements: ?{ [ElementTypes]: StripeElement },
  shouldSecurePayment: ?boolean,
  stripeChargeAmount: number,
  userDetails?: UserDetails,
  updateState: (UpdateStateParams) => any,
  cancelAuthTrigger?: { cancel: boolean },
|}): Promise<StripeSource> => {
  // Check that stripe was initialized
  if (!stripe || !elements) {
    throw new StripeError(ERROR_CODES.stripe_not_initialized)
  }
  const source = await createRawStripeSource({ stripe, elements, userDetails })

  // Exit with 3ds not supported if it is required
  if (!isSourceSupportThreeDS(source) && shouldSecurePayment) {
    throw new StripeError(ERROR_CODES.threed_secure_not_supported, source)
  }

  // Go through 3ds if needed
  if (isSourceRequireThreeDS(source) || shouldSecurePayment) {
    const authorizedSource = await authorizeSource({
      stripe,
      amount: stripeChargeAmount,
      currency,
      source,
      updateState,
      cancelAuthTrigger,
    })
    return authorizedSource
  }

  return source
}

/**
 * Authorize given source with given type. Originally it was aimed to work
 * for 3DS autorization, but it also could be used to authorize different type
 * of sources, like "alipay"
 */
export const authorizeSource = async (params: {|
  stripe: StripeObject,
  amount: number,
  currency: string,
  source?: StripeSource,
  updateState: (UpdateStateParams) => any,
  cancelAuthTrigger?: { cancel: boolean },
  type?: string,
|}): Promise<StripeSource> => {
  // Get 3DS source
  const createResult = await params.stripe.createSource({
    type: params.type || 'three_d_secure',
    three_d_secure: params.source ? { card: params.source.id } : null,
    amount: params.amount,
    currency: params.currency,
    redirect: { return_url: 'https://www.mozio.com/3ds_success.html' },
  })

  if (createResult.error) {
    const { error } = createResult
    throw new StripeError(error.code, params.source, error.message)
  }

  params.updateState({
    show3DSecure: true,
    threeDSecureURL: (createResult.source.redirect || {}).url,
  })

  return waitSourceAuthorization(createResult, params)
}

/**
 * Tail recursion implementation for waiting the source authorization by
 * the user
 * @param  {Source}  createResult Initial source that should be authorized with 3DS
 * @param  {Object}  params       Parameters that was passed to the authorizeSource
 * @return {Promise}
 */
const waitSourceAuthorization = async (createResult, params) => {
  const { stripe, updateState, cancelAuthTrigger } = params

  // We are using trigger object with boolean inside to notify the
  // loop that we no longer need to wait for the source
  if (cancelAuthTrigger && cancelAuthTrigger.cancel) {
    throw new StripeError(
      ERROR_CODES.threed_secure_canceled,
      createResult.source
    )
  }

  // eslint-disable-next-line no-await-in-loop
  const retriveResult = await stripe.retrieveSource({
    id: createResult.source.id,
    client_secret: createResult.source.client_secret,
  })

  if (retriveResult.error) {
    const { error } = retriveResult
    updateState({ show3DSecure: false, threeDSecureURL: '' })
    throw new StripeError(error.code, createResult.source, error.message)
  }

  switch (retriveResult.source.status) {
    case 'pending':
      await delay(1000)
      return waitSourceAuthorization(createResult, params)
    case 'chargeable':
      updateState({ show3DSecure: false, threeDSecureURL: '' })
      return retriveResult.source
    case 'failed':
    default:
      updateState({ show3DSecure: false, threeDSecureURL: '' })
      throw new StripeError(
        ERROR_CODES.threed_secure_declined,
        retriveResult.source
      )
  }
}

/**
 * Function to extract appropreate stripe config object from the
 * configs array based on given currency name. For instance,
 * the configs array might have a config for a few currencies and one
 * of the configs marked as default config for all other currencies. This
 * function find the config for the given currency or the config marked as default
 */
export const getConfigByCurrency = (
  stripeConfigs: Array<StripeConfig>,
  currency: string
): StripeConfig => {
  const fallbackConfig = stripeConfigs.find((x) => x.default)
  if (!fallbackConfig) {
    throw new Error(`There is no default config defined`)
  }

  for (let i = 0; i < stripeConfigs.length; i++) {
    const conf = stripeConfigs[i]
    if (conf.currencies.indexOf(currency.toUpperCase()) >= 0) {
      return conf
    }
  }

  return fallbackConfig
}
